import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { service_provider_api_requests, SERVICE_PROVIDER_API } from "..";
import { useNotification } from "../../../components/common/GlobalNotification";
import { ServiceProvider } from "../service-provider";

const PATH = "/favorites";
const BASE_QUERY_KEY = SERVICE_PROVIDER_API + PATH;

export interface UserFavoriteDTO {
  id: number;
  userId: number;
  serviceProviderId: number;
  createTimestamp?: number;
}

const IDS_QUERY_KEY = [BASE_QUERY_KEY, "ids"] as const;
const LIST_QUERY_KEY = [BASE_QUERY_KEY, "list"] as const;

const useAddFavorite = () => {
  const queryClient = useQueryClient();
  const { setSuccessNotification, setErrorNotification } = useNotification();

  return useMutation({
    mutationFn: async ({
      serviceProviderId,
    }: {
      serviceProviderId: number;
    }): Promise<UserFavoriteDTO> =>
      service_provider_api_requests.post(`${PATH}/${serviceProviderId}`),

    onSuccess: (_data, { serviceProviderId }) => {
      setSuccessNotification("Added to favorites");
      // Update the IDs cache directly — no extra API call needed
      queryClient.setQueryData<number[]>(IDS_QUERY_KEY, (prev) =>
        prev ? [...prev, serviceProviderId] : [serviceProviderId],
      );
      // Full list (My Favourites page) needs a real refetch since we don't have the full SP object
      queryClient.invalidateQueries({ queryKey: LIST_QUERY_KEY });
    },

    onError: () => {
      setErrorNotification("Failed to add to favorites");
    },
  });
};

const useRemoveFavorite = () => {
  const queryClient = useQueryClient();
  const { setSuccessNotification, setErrorNotification } = useNotification();

  return useMutation({
    mutationFn: async ({
      serviceProviderId,
    }: {
      serviceProviderId: number;
    }): Promise<void> =>
      service_provider_api_requests.delete(`${PATH}/${serviceProviderId}`),

    onSuccess: (_data, { serviceProviderId }) => {
      setSuccessNotification("Removed from favorites");
      // Update the IDs cache directly — no extra API call needed
      queryClient.setQueryData<number[]>(IDS_QUERY_KEY, (prev) =>
        prev ? prev.filter((id) => id !== serviceProviderId) : [],
      );
      // Full list (My Favourites page) needs a real refetch
      queryClient.invalidateQueries({ queryKey: LIST_QUERY_KEY });
    },

    onError: () => {
      setErrorNotification("Failed to remove from favorites");
    },
  });
};

const useGetFavorites = (enabled: boolean = true) => {
  return useQuery<ServiceProvider[]>({
    queryKey: LIST_QUERY_KEY,
    queryFn: async (): Promise<ServiceProvider[]> =>
      service_provider_api_requests.get(PATH),
    enabled,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
};

/** Lightweight — returns only the SP IDs the user has favorited. Used on search page. */
const useGetFavoriteIds = (enabled: boolean = true) => {
  return useQuery<number[]>({
    queryKey: IDS_QUERY_KEY,
    queryFn: async (): Promise<number[]> =>
      service_provider_api_requests.get(`${PATH}/ids`),
    enabled,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

export {
  BASE_QUERY_KEY as FAVORITES_QUERY_KEY,
  useAddFavorite,
  useRemoveFavorite,
  useGetFavorites,
  useGetFavoriteIds,
};
